import { type ReactElement, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { ErrorState } from "./state-components/error-state";
import { EmptyState } from "./state-components/empty-state";
import { usePageLayoutState } from "./hooks/use-page-layout-state";
import { PageHeader } from "./shared-components/page-header";
import { PageWrapper } from "./shared-components/page-wrapper";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@shared-kernel/primary/shared/shadcn/ui/input-group";
import type { CardListPageLayoutProps } from "./types";

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const SearchInput = ({ value, onChange, placeholder = "Rechercher..." }: SearchInputProps) => (
  <InputGroup>
    <InputGroupAddon>
      <Search />
    </InputGroupAddon>
    <InputGroupInput type="search" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
  </InputGroup>
);

/**
 * CardListPageLayout - Reusable layout for list pages with CardList/CardListSection
 *
 * Handles loading, error, empty, and success states automatically.
 * Enforces array type for data to ensure type safety with card list components.
 *
 * @example
 * ```tsx
 * const FundingRequests = () => {
 *   const { data, fetching } = useAppSelector(selectFundingRequests);
 *
 *   return (
 *     <CardListPageLayout
 *       fetching={fetching}
 *       data={data}
 *       title="Demandes de financement"
 *       loadingComponent={<FundingRequestsSkeleton />}
 *       emptyStateProps={{
 *         title: "Aucune demande",
 *         description: "Vous n'avez pas encore de demandes de financement"
 *       }}
 *     >
 *       {(requests) => (
 *         <CardListSection
 *           title="Historique"
 *           items={requests}
 *           getGroupingKeyAndLabel={r => formatDate(r.date)}
 *           getSortDate={r => r.date}
 *           getKey={r => r.id}
 *           renderItem={r => <FundingRequestCard request={r} />}
 *         />
 *       )}
 *     </CardListPageLayout>
 *   );
 * };
 * ```
 */
export function CardListPageLayout<T>({
  fetching,
  data,
  title,
  subtitle,
  action,
  search,
  pendingActionsSection,
  children,
  loadingComponent,
  errorStateProps,
  emptyStateProps,
  className,
}: CardListPageLayoutProps<T>): ReactElement {
  const [searchValue, setSearchValue] = useState("");
  const hasData = data !== null && data !== undefined;
  const isEmptyArray = Array.isArray(data) && data.length === 0;
  const { isLoading, isError, isSuccess } = usePageLayoutState({
    fetching,
    idleAsLoading: true,
  });

  const hasSearch = search !== undefined;

  // Compute filtered data
  const filteredData = useMemo(() => {
    if (!data || !hasSearch || !searchValue.trim()) return data;
    return data.filter(item => search.filterFn(item, searchValue));
  }, [data, search, hasSearch, searchValue]);

  const isFilteredEmpty = Array.isArray(filteredData) && filteredData.length === 0;
  const hasSearchValue = hasSearch && searchValue.trim();

  // Render loading state
  if (isLoading) {
    return (
      <PageWrapper className={className}>
        <PageHeader title={title} subtitle={subtitle} action={action} />
        {hasSearch && <SearchInput value={searchValue} onChange={setSearchValue} placeholder={search.placeholder} />}
        {loadingComponent}
      </PageWrapper>
    );
  }

  // Render error state
  if (isError) {
    return (
      <PageWrapper className={className}>
        <PageHeader title={title} subtitle={subtitle} action={action} />
        {hasSearch && <SearchInput value={searchValue} onChange={setSearchValue} placeholder={search.placeholder} />}
        <ErrorState {...errorStateProps} />
      </PageWrapper>
    );
  }

  // Render empty state (no data at all)
  if (isSuccess && (!hasData || isEmptyArray)) {
    return (
      <PageWrapper className={className}>
        <PageHeader title={title} subtitle={subtitle} action={action} />
        {hasSearch && <SearchInput value={searchValue} onChange={setSearchValue} placeholder={search.placeholder} />}
        {pendingActionsSection}
        <EmptyState {...emptyStateProps} />
      </PageWrapper>
    );
  }

  // Render search empty state (data exists but filtered is empty)
  if (isSuccess && hasSearchValue && isFilteredEmpty) {
    return (
      <PageWrapper className={className}>
        <PageHeader title={title} subtitle={subtitle} action={action} />
        <SearchInput value={searchValue} onChange={setSearchValue} placeholder={search.placeholder} />
        {pendingActionsSection}
        <EmptyState {...search.searchEmptyStateProps} />
      </PageWrapper>
    );
  }

  // Render success state with data
  return (
    <PageWrapper className={className}>
      <PageHeader title={title} subtitle={subtitle} action={action} />
      {hasSearch && <SearchInput value={searchValue} onChange={setSearchValue} placeholder={search.placeholder} />}
      {pendingActionsSection}
      {children(filteredData!)}
    </PageWrapper>
  );
}
